import React from "react";
import { PermissionsCollection } from "./collection";

// Default with false & no permissions, so if there is no provider in the stack, then nothing can be permitted
export const PermissionsContext = React.createContext({ hasPermission: () => false, permissions: [] });

const mapPermissionsToProps = ({ list }) => ({ permissions: list });
const permissionsConnect = PermissionsCollection.connect(mapPermissionsToProps);

// This is where we will actually do the logic for permission checking
function hasPermissionWithLogic(permissions, permission) {
  if (typeof permission !== "string") {
    // Always false if the permission isn't even a string
    // should maybe be a warning
    if (permission != null) {
      console.warn("Invalid value provided to hasPermission", permission);
    }
    return false;
  }
  function normaliseKey(key) {
    return key.toLowerCase();
  }
  const normalisedPermission = normaliseKey(permission);
  const found = permissions
    .find(permission => normaliseKey(permission.key) === normalisedPermission);
  return !!(found && found.permissive);
}

// Use this at the highest level possible
// We allow `connect` to be passed here as well in the case where there is an escape needed
export const wrapPermissionsProvider = (WrappedComponent, connect = permissionsConnect) => {

  function getPermissionMap(permissions) {
    // We only care about the permissive permissions
    return permissions
      .filter(({ permissive }) => permissive)
      .reduce(
        (map, { key, permissive }) => {
          map[key] = permissive;
          return map;
        },
        {}
      );
  }

  function comparePermissionProps({ permissions: a }, { permissions: b }) {
    if (!a && !b) {
      // Both null
      return true;
    }
    if (!a || !b) {
      // One null
      return false;
    }
    const mapA = getPermissionMap(a),
      mapB = getPermissionMap(b),
      // We need to get the keys of at least one map, so might as well do
      // both as we can just check the length of both and be done
      mapKeysA = Object.keys(mapA),
      mapKeysB = Object.keys(mapB);

    // Different lengths means one has an extra permission, so not equal
    if (mapKeysA.length !== mapKeysB.length) {
      return false;
    }

    const foundDifferentIndex = mapKeysA
      // mapB won't have the value true if it is different
      .findIndex(key => !mapB[key]);
    // If no different keys are found, the permissions are identical
    return foundDifferentIndex === -1;
  }

  /**
   * We're going to have this so we can create a context that our permissions
   * can be pulled from
   *
   * We're still going to load permissions by way of a collection,
   * but its going to be in a way that we don't need to think about it everywhere
   * permissions are used.
   *
   * We're going functional here because this should be pretty static
   */
  const PermissionsProviderWrapper = React.memo((props) => {
    const permissions = props.permissions || [];

    function hasPermissionWrapped(permission) {
      return hasPermissionWithLogic(permissions, permission);
    }

    const baseProps  = { hasPermission: hasPermissionWrapped, permissions };

    return (
      <PermissionsContext.Provider value={baseProps}>
        <WrappedComponent {...props} {...baseProps} />
      </PermissionsContext.Provider>
    );
  }, comparePermissionProps);

  return connect(PermissionsProviderWrapper);
};

// This is so we can be used inside a renderer function (class or functional)
export const PermissionsConsumer = PermissionsContext.Consumer;

// This can be used for class components
export const wrapPermissions = (WrappedComponent) => {
  return (originalProps) => {
    const newProps = React.useContext(PermissionsContext);
    return <WrappedComponent {...originalProps} {...newProps} />
  };
};

// This can be used _within_ functional components
export const hasPermission = (permission) => {
  const context = React.useContext(PermissionsContext);
  return context.hasPermission(permission);
};

export {
  wrapPermissionsProvider as withPermissionsProvider,
  wrapPermissions as withPermissions
}



// WEBPACK FOOTER //
// ./src/services/permissions.js